<template>
  <section v-if="data" class="flex flex-col gap-5">
    <h2 class="text-2xl font-bold text-white">Quarterly Burn Status</h2>

    <div class="flex gap-8 mobile:flex-col">
      <!-- 왼쪽 컬럼: 이전 분기 + LIFETIME 카드 -->
      <div class="flex flex-1 flex-col gap-8">
        <!-- 이전 분기 카드 -->
        <div class="bg-white/5 border border-white/20 rounded-lg p-5 flex flex-col gap-5">
          <div class="flex items-center justify-between">
            <span class="bg-white/10 px-2 py-1 rounded text-lg font-medium text-white">
              {{ data.prevQuarter }}
            </span>
            <!-- 상태 태그: ESTIMATED/ACTUAL -->
            <span
              v-if="data.prevBurnStatus === 'ESTIMATED'"
              class="px-2 py-1 rounded text-lg font-medium flex items-center gap-2 bg-[#ff9800]/10 text-[#ff9800]"
            >
              <span class="w-2 h-2 rounded-full bg-[#ff9800]" />
              Estimated
            </span>
            <span
              v-else
              class="text-base font-normal text-white/60 text-right"
            >
              Permanently removed from supply
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-lg font-bold text-white/40">Previous Burn</span>
            <div class="flex items-center gap-1">
              <RollingNumber
                :value="data.prevBurn"
                :decimals="2"
                class="text-2xl font-bold text-white"
              />
              <span class="text-2xl font-bold text-white/40">NXPC</span>
            </div>
          </div>
        </div>

        <!-- LIFETIME 카드 -->
        <div class="bg-white/5 border border-white/20 rounded-lg p-5 flex flex-col gap-5">
          <div class="flex items-center justify-between">
            <span class="bg-white/10 px-2 py-1 rounded text-lg font-medium text-white">
              LIFETIME
            </span>
            <span class="text-base font-normal text-white/60 text-right ml-auto">
              Permanently removed from supply
            </span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-lg font-bold text-white/40">Total Burned</span>
            <div class="flex items-center gap-1">
              <RollingNumber
                :value="data.totalBurned"
                :decimals="2"
                class="text-2xl font-bold text-white"
              />
              <span class="text-2xl font-bold text-white/40">NXPC</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 오른쪽 컬럼: 현재 분기 카드 -->
      <div class="flex flex-1">
        <div class="bg-white/5 border border-white/20 rounded-lg p-5 flex flex-col gap-5 w-full">
          <!-- 상단 뱃지 -->
          <div class="flex items-start justify-between">
            <span class="bg-point-color/10 px-2 py-1 rounded text-lg font-medium text-point-color">
              {{ data.currentQuarter }}
            </span>
            <span class="bg-explorer-green/10 px-2 py-1 rounded text-lg font-medium text-explorer-green flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-explorer-green" />
              Live Accumulation
            </span>
          </div>

          <!-- QTD Burn Projection -->
          <div class="flex flex-col gap-1">
            <span class="text-lg font-bold text-white/40">QTD Burn Projection</span>
            <div class="flex items-center gap-1">
              <RollingNumber
                :value="data.qtdBurnProjection"
                :decimals="2"
                class="text-[28px] font-bold text-white"
              />
              <span class="text-2xl font-bold text-white/40">NXPC</span>
            </div>
          </div>

          <!-- Progress Section -->
          <div class="flex flex-col gap-4 pt-5 border-t border-white/10 flex-1 justify-center">
            <div class="flex items-center justify-between">
              <span class="text-lg font-bold text-white/40">Quarter Progress</span>
              <span class="text-[18px] font-bold text-white">
                <RollingNumber
                  :value="data.quarterProgress"
                  :decimals="2"
                  class="inline"
                />% Time Elapsed
              </span>
            </div>

            <!-- Progress Bar -->
            <div class="w-full h-5 rounded-2xl bg-white/10 overflow-hidden">
              <div
                class="h-full bg-point-color rounded-l-2xl transition-all duration-300"
                :style="{ width: `${data.quarterProgress}%` }"
              />
            </div>

            <div class="flex items-center justify-between">
              <span class="text-lg font-bold text-white/40">{{ formatDate(data.quarterStartDate) }}</span>
              <span class="text-lg font-bold text-white/40">Target : {{ formatDate(data.targetDate) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { BurnOverviewView } from "~/api/service/livewatch/burn-overview.service";
import RollingNumber from "~/ui/RollingNumber/RollingNumber.vue";

defineProps<{
  data: BurnOverviewView | undefined;
}>();

const formatDate = (date: Date) => {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
</script>
